import type { CSSProperties } from 'react'
import { Box } from '@chakra-ui/react'
import { Link } from 'react-router-dom'

/** Neon comic 3D extrusion: down-right, lime palette, black stroke */
function extrusionShadow(steps: number, depth: number): string {
  const parts: string[] = []
  for (let i = 1; i <= steps; i++) {
    const t = i / steps
    const x = Math.round(depth * t)
    const y = Math.round(depth * t)
    // Deep yellow-green → bright neon lime (halftone-ish banding)
    const r = Math.round(95 + 160 * t)
    const g = Math.round(175 + 80 * t)
    const b = Math.round(0 + 55 * t)
    parts.push(`${x}px ${y}px 0 rgb(${r},${g},${b})`)
  }
  return parts.join(', ')
}

const LABEL = '3DYAZDIR.COM'

type BrandLogoProps = {
  /** Target visual height (px) — wordmark scales to fit */
  size?: number
  /** Kept for API compatibility; wordmark is always the 3D logotype */
  showWordmark?: boolean
  asLink?: boolean
  wordmarkSize?: 'sm' | 'md' | 'lg'
}

const sizeScale: Record<NonNullable<BrandLogoProps['wordmarkSize']>, number> = {
  sm: 0.85,
  md: 1,
  lg: 1.12,
}

export default function BrandLogo({
  size = 36,
  showWordmark: _showWordmark = true,
  asLink = true,
  wordmarkSize = 'md',
}: BrandLogoProps) {
  const scale = sizeScale[wordmarkSize]
  const depth = Math.max(6, Math.round((size * 0.2) * scale))
  const steps = Math.min(28, Math.max(14, Math.round(depth * 1.2)))
  const fontSize = Math.max(11, Math.round(size * 0.38 * scale))
  const stroke = Math.max(1, Math.round(fontSize * 0.045))

  const textStyle: CSSProperties = {
    fontFamily: '"Bungee", "Staatliches", system-ui, sans-serif',
    fontSize: `clamp(14px, 2.8vw + 0.35rem, ${fontSize}px)`,
    lineHeight: 1,
    letterSpacing: '-0.03em',
    textTransform: 'uppercase',
    color: '#EFFFAA',
    WebkitTextStroke: `${stroke}px #0a0a0a`,
    paintOrder: 'stroke fill',
    textShadow: extrusionShadow(steps, depth),
  }

  const inner = (
    <Box as="span" display="inline-block" whiteSpace="nowrap" maxW={{ base: 'min(92vw, 520px)', md: 'none' }}>
      <Box as="span" style={textStyle}>
        {LABEL}
      </Box>
    </Box>
  )

  if (asLink) {
    return (
      <Link to="/tr/anasayfa" style={{ textDecoration: 'none', display: 'inline-flex' }}>
        {inner}
      </Link>
    )
  }
  return inner
}
